/**
 * 24K.Infinity — MUI theme factory
 * -------------------------------------------------------------------------
 * Turns the design tokens into a live MUI 7 theme (light or dark) with the
 * component overrides that encode the GeoTab-inspired language:
 *   cobalt primary · ink secondary · hairline borders · near-flat elevation
 *   · Swiss inputs · segmented controls · thin progress · tabular numerals.
 *
 *   import { createInfinityTheme } from './design-system';
 *   const theme = createInfinityTheme('light');
 * -------------------------------------------------------------------------
 */
import { createTheme } from '@mui/material/styles';
import {
  cobalt,
  neutral,
  semantic,
  signature,
  dataviz,
  surfaces,
  fontFamily,
  fontWeight,
  typeScale,
  numeric,
  radius,
  shadow,
  sizing,
  motion,
  breakpoints as bp,
  zIndex as z,
} from './tokens';

const rem = (px) => `${px / 16}rem`;

// Resolve against the active theme so the pilot's palette is respected too.
// The stronger selector keeps its generic translucent Paper rule from making
// dialogs transparent enough to read the page underneath.
const popupPaper = ({ theme }) => ({
  '&&': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18,26,43,0.90)' : 'rgba(250,252,255,0.92)',
    backgroundImage: 'none',
    color: theme.palette.text.primary,
    backdropFilter: 'blur(28px) saturate(1.35)',
    WebkitBackdropFilter: 'blur(28px) saturate(1.35)',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.palette.mode === 'dark' ? '0 18px 48px rgba(0,0,0,0.38)' : '0 18px 44px rgba(20,30,60,0.16)',
    '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
      backgroundColor: theme.palette.mode === 'dark' ? '#121A2B' : '#FAFCFF',
    },
    '@media (prefers-reduced-transparency: reduce)': {
      backgroundColor: theme.palette.mode === 'dark' ? '#121A2B' : '#FAFCFF',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
  },
});

// Map a token type-style → MUI typography variant (px → rem).
const variant = (t) => ({
  fontSize: rem(t.fontSize),
  lineHeight: t.lineHeight,
  fontWeight: t.fontWeight,
  ...(t.letterSpacing ? { letterSpacing: t.letterSpacing } : {}),
  ...(t.textTransform ? { textTransform: t.textTransform } : {}),
  ...(t.fontFamily ? { fontFamily: t.fontFamily } : {}),
});

export function createInfinityTheme(mode = 'light') {
  const s = surfaces[mode] || surfaces.light;
  const isDark = mode === 'dark';
  const accent = s.accent;

  const theme = createTheme({
    palette: {
      mode,
      common: { black: neutral.black, white: neutral[0] },
      primary: {
        main: accent,
        light: cobalt[400],
        dark: cobalt[700],
        contrastText: '#FFFFFF',
      },
      // "secondary" is the periwinkle accent-dk (#6C6AFF) from the reference.
      secondary: {
        main: cobalt[400],
        light: cobalt[300],
        dark: cobalt[500],
        contrastText: '#FFFFFF',
      },
      success: semantic.success,
      warning: semantic.warning,
      error: semantic.error,
      info: semantic.info,
      background: { default: s.appBg, paper: s.paper },
      text: {
        primary: s.text.primary,
        secondary: s.text.secondary,
        disabled: s.text.disabled,
      },
      divider: s.border,
      action: {
        active: s.text.secondary,
        hover: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(20,20,20,0.04)',
        selected: s.accentSoft,
        disabled: s.text.disabled,
        focus: s.focusRing,
      },
      // --- custom slots (read via theme.palette.* in sx / primitives) ---
      surface: s,
      cobalt,
      neutral,
      signature,
    },

    // Expose the full dataviz palette + raw tokens on the theme.
    dataviz,
    tokens: { radius, shadow, sizing, motion },

    shape: { borderRadius: radius.md },

    breakpoints: { values: bp },
    zIndex: z,

    typography: {
      fontFamily: fontFamily.sans,
      fontWeightLight: fontWeight.light,
      fontWeightRegular: fontWeight.regular,
      fontWeightMedium: fontWeight.medium,
      // Reference is Light/Regular/Medium only — cap "bold" at Medium.
      fontWeightBold: fontWeight.medium,
      htmlFontSize: 16,
      display1: variant(typeScale.display1),
      display2: variant(typeScale.display2),
      h1: variant(typeScale.h1),
      h2: variant(typeScale.h2),
      h3: variant(typeScale.h3),
      h4: variant(typeScale.h4),
      h5: variant(typeScale.h5),
      h6: variant(typeScale.h6),
      subtitle1: variant(typeScale.subtitle1),
      subtitle2: variant(typeScale.subtitle2),
      body1: variant(typeScale.body1),
      body2: variant(typeScale.body2),
      caption: variant(typeScale.caption),
      overline: variant(typeScale.overline),
      button: variant(typeScale.button),
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' },
          body: {
            backgroundColor: s.appBg,
            color: s.text.primary,
            fontFamily: fontFamily.sans,
          },
          '::selection': { backgroundColor: accent, color: '#FFFFFF' },
          // tabular numerals everywhere figures matter most
          '.tnum': { fontVariantNumeric: numeric.fontVariantNumeric },
          a: { color: accent, textDecoration: 'none' },
        },
      },

      // Register the custom display variants so they render as block elements.
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            display1: 'h1',
            display2: 'h2',
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            h4: 'h4',
            h5: 'h5',
            h6: 'h6',
            subtitle1: 'h6',
            subtitle2: 'h6',
            body1: 'p',
            body2: 'p',
            caption: 'span',
            overline: 'span',
            button: 'span',
            inherit: 'p',
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: s.border },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: s.paper,
            border: `1px solid ${s.border}`,
            borderRadius: radius.lg,
            boxShadow: 'none',
          },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(10,10,11,0.82)' : 'rgba(255,255,255,0.82)',
            color: s.text.primary,
            backdropFilter: 'saturate(1.2) blur(10px)',
            borderBottom: `1px solid ${s.border}`,
            boxShadow: 'none',
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: s.paper, borderColor: s.border },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            textTransform: 'none',
            fontWeight: fontWeight.medium,
            paddingInline: 16,
          },
          sizeSmall: { paddingInline: 12, height: sizing.controlHeight.sm },
          sizeMedium: { height: sizing.controlHeight.md },
          sizeLarge: { height: sizing.controlHeight.lg, paddingInline: 22 },
          containedPrimary: {
            backgroundColor: accent,
            '&:hover': { backgroundColor: s.accentHover },
          },
          containedSecondary: {
            backgroundColor: cobalt[400],
            color: '#FFFFFF',
            '&:hover': { backgroundColor: cobalt[500] },
          },
          outlined: {
            borderColor: s.borderStrong,
            color: s.text.primary,
            '&:hover': { borderColor: s.text.primary, backgroundColor: s.sunken },
          },
          text: {
            color: accent,
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          },
        },
      },

      MuiButtonBase: {
        styleOverrides: {
          root: {
            // Paper-offset + solid-accent ring: meets the 3:1 non-text minimum
            // and stays visible even on cobalt-filled controls.
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 2px ${s.paper}, 0 0 0 4px ${accent}`,
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: { root: { borderRadius: radius.md, color: s.text.secondary } },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            backgroundColor: s.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: s.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: s.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: accent,
              borderWidth: 1.5,
            },
          },
          input: { '&::placeholder': { color: s.text.disabled, opacity: 1 } },
        },
      },

      MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: accent } } } },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            height: 24,
            fontSize: rem(12),
            fontWeight: fontWeight.medium,
            letterSpacing: '0.01em',
          },
          label: { paddingInline: 8 },
          outlined: { borderColor: s.border },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: neutral[900],
            color: '#F4F5F7',
            borderRadius: radius.sm,
            fontSize: rem(12),
            paddingInline: 10,
            paddingBlock: 6,
          },
          arrow: { color: neutral[900] },
        },
      },

      MuiDivider: { styleOverrides: { root: { borderColor: s.border } } },

      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: s.border },
          head: {
            color: s.text.secondary,
            fontSize: rem(11),
            fontWeight: fontWeight.semibold,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 6, borderRadius: radius.pill, backgroundColor: s.sunken },
          bar: { borderRadius: radius.pill, backgroundColor: accent },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: accent, height: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: fontWeight.medium,
            minHeight: 44,
            color: s.text.secondary,
            '&.Mui-selected': { color: s.text.primary },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: fontWeight.medium,
            border: 'none',
            color: s.text.secondary,
            borderRadius: radius.sm,
            '&.Mui-selected': { color: s.text.primary },
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            '&.Mui-selected': {
              backgroundColor: s.accentSoft,
              color: accent,
              '&:hover': { backgroundColor: s.accentSoft },
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: popupPaper,
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: isDark ? 'rgba(2,6,14,0.40)' : 'rgba(20,30,50,0.18)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              '@media (prefers-reduced-transparency: reduce)': {
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
              },
            },
          },
        },
      },
      MuiPopover: { styleOverrides: { paper: popupPaper } },
      MuiAutocomplete: { styleOverrides: { paper: popupPaper } },
      MuiMenu: {
        styleOverrides: {
          paper: (props) => ({ ...popupPaper(props), borderRadius: radius.md }),
          list: { padding: 6 },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { '& .Mui-checked': { color: accent } },
          track: { backgroundColor: s.borderStrong },
        },
      },
    },
  });

  return theme;
}

export default createInfinityTheme;
